import type { ActivityCostBucket, AiUsageActivity } from "./aiUsageActivities";
import {
  addRecordToActivityBuckets,
  classifyAiUsageActivity,
  emptyActivityBuckets,
} from "./aiUsageActivities";
import type { AiUsageRecord } from "./aiUsage";
import { formatEstimatedCostUsd, recordCost } from "./aiUsage";

export const DEFAULT_PRODUCTIVITY_HOURLY_RATE_USD = 75;

// Activities that contribute to a workflow's dollar value. Monitor (autofix/
// autotune) is intentionally excluded: we can't count episodes accurately yet.
export const VALUE_ACTIVITIES = ["build", "run"] as const;
export type ValueActivity = (typeof VALUE_ACTIVITIES)[number];

export type WorkflowValueWorkflow = {
  id: string;
  name: string;
  spaceId: string;
  estimatedMinutesSaved: number | null;
  estimatedBuildMinutesSaved: number | null;
  timeSavedSource: "llm" | "manual" | null;
  timeSavedBuildSource: "llm" | "manual" | null;
};

export type WorkflowActivityValue = {
  aiSpendUsd: number | null;
  eventCount: number;
  minutesPerEvent: number | null;
  source: "llm" | "manual" | null;
  grossValueUsd: number | null;
};

export type WorkflowAiValueRow = {
  workflowId: string;
  workflowName: string;
  spaceId: string;
  byActivity: Record<ValueActivity, WorkflowActivityValue>;
  totalGrossValueUsd: number | null;
};

export type AiValueSummary = {
  grossValueUsd: number | null;
  workflowCount: number;
};

function minutesToUsd(minutes: number, hourlyRateUsd: number) {
  return (minutes / 60) * hourlyRateUsd;
}

function workflowMinutesForActivity(
  workflow: WorkflowValueWorkflow,
  activity: ValueActivity
): { minutes: number | null; source: "llm" | "manual" | null } {
  if (activity === "build") {
    return {
      minutes: workflow.estimatedBuildMinutesSaved,
      source: workflow.timeSavedBuildSource,
    };
  }
  return {
    minutes: workflow.estimatedMinutesSaved,
    source: workflow.timeSavedSource,
  };
}

export function computeWorkflowAiValueRows(
  usageRecords: readonly AiUsageRecord[],
  workflows: readonly WorkflowValueWorkflow[],
  executionCounts: ReadonlyMap<string, number>,
  prices: ReadonlyMap<string, number>,
  hourlyRateUsd: number
): WorkflowAiValueRow[] {
  const workflowById = new Map(workflows.map((w) => [w.id, w]));
  const byWorkflow = new Map<
    string,
    {
      spendByActivity: Record<AiUsageActivity, ActivityCostBucket>;
      hasBuildUsage: boolean;
    }
  >();

  for (const record of usageRecords) {
    if (!record.workflowId) continue;
    const activity = classifyAiUsageActivity(record.source);
    if (!activity) continue;

    const bucket = byWorkflow.get(record.workflowId) ?? {
      spendByActivity: emptyActivityBuckets(),
      hasBuildUsage: false,
    };
    const cost = recordCost(record, prices);
    addRecordToActivityBuckets(bucket.spendByActivity, record, cost);
    if (activity === "build") bucket.hasBuildUsage = true;
    byWorkflow.set(record.workflowId, bucket);
  }

  // Run value is driven by execution counts, not AI usage, so include workflows
  // that ran in the window even if they recorded no AI usage.
  for (const workflowId of executionCounts.keys()) {
    if (!byWorkflow.has(workflowId)) {
      byWorkflow.set(workflowId, {
        spendByActivity: emptyActivityBuckets(),
        hasBuildUsage: false,
      });
    }
  }

  return [...byWorkflow.entries()]
    .map(([workflowId, data]) => {
      const workflow = workflowById.get(workflowId);
      if (!workflow) return null;

      const byActivity = {} as Record<ValueActivity, WorkflowActivityValue>;
      let totalGross = 0;
      let hasGross = false;

      for (const activity of VALUE_ACTIVITIES) {
        const spendBucket = data.spendByActivity[activity];
        const { minutes, source } = workflowMinutesForActivity(
          workflow,
          activity
        );
        // Build value is one-time; run minutes are per successful execution.
        const eventCount =
          activity === "build"
            ? data.hasBuildUsage
              ? 1
              : 0
            : (executionCounts.get(workflowId) ?? 0);
        const grossValueUsd =
          minutes != null && eventCount > 0
            ? minutesToUsd(
                activity === "build" ? minutes : minutes * eventCount,
                hourlyRateUsd
              )
            : null;
        if (grossValueUsd != null) {
          totalGross += grossValueUsd;
          hasGross = true;
        }

        byActivity[activity] = {
          aiSpendUsd: spendBucket.estimatedCostUsd,
          eventCount,
          minutesPerEvent: minutes,
          source,
          grossValueUsd,
        };
      }

      return {
        workflowId,
        workflowName: workflow.name,
        spaceId: workflow.spaceId,
        byActivity,
        totalGrossValueUsd: hasGross ? totalGross : null,
      };
    })
    .filter(
      (row): row is WorkflowAiValueRow =>
        row != null && row.totalGrossValueUsd != null
    )
    .sort(
      (a, b) =>
        (b.totalGrossValueUsd ?? 0) - (a.totalGrossValueUsd ?? 0) ||
        a.workflowName.localeCompare(b.workflowName)
    );
}

export function computeAiValueSummary(
  rows: readonly WorkflowAiValueRow[],
  activityFilter: ValueActivity | "all" = "all"
): AiValueSummary {
  let grossValueUsd = 0;
  let hasGross = false;
  let workflowCount = 0;

  for (const row of rows) {
    const activities =
      activityFilter === "all" ? VALUE_ACTIVITIES : [activityFilter];
    let rowHasValue = false;
    for (const activity of activities) {
      const value = row.byActivity[activity].grossValueUsd;
      if (value != null) {
        grossValueUsd += value;
        hasGross = true;
        rowHasValue = true;
      }
    }
    if (rowHasValue) workflowCount += 1;
  }

  return {
    grossValueUsd: hasGross ? grossValueUsd : null,
    workflowCount,
  };
}

export function formatValueUsd(amount: number | null): string {
  return formatEstimatedCostUsd(amount);
}

export function formatSignedValueUsd(amount: number | null): string {
  const formatted = formatValueUsd(amount);
  return amount != null && amount > 0 ? `+${formatted}` : formatted;
}

export function productivityRateFromBudgets(
  budgets: readonly {
    scopeKind: string;
    aiProviderId: string | null;
    productivityHourlyRate: number | null;
  }[]
): number {
  const tenantBudget = budgets.find(
    (b) => b.scopeKind === "tenant" && b.aiProviderId == null
  );
  const rate = tenantBudget?.productivityHourlyRate;
  return rate != null && rate > 0 ? rate : DEFAULT_PRODUCTIVITY_HOURLY_RATE_USD;
}
